// The approved sitemap (design/admin-portal-sitemap.html, v0.1) as data.
// Every page below is a routed stub until its area is drilled down and built;
// the stub renders this metadata so the running app IS the sitemap.

export type Page = {
  /** route path relative to its scope root ('' = the scope root itself) */
  path: string;
  title: string;
  /** one-line responsibility, straight from the sitemap document */
  blurb: string;
  /** which v3 pages this absorbs (context for review; '' = new in v4) */
  absorbs: string;
};

export type Area = {
  key: string;
  label: string;
  /** nav target relative to the store root */
  nav: string;
  pages: Page[];
};

export const STORE_AREAS: Area[] = [
  {
    key: 'overview',
    label: 'Overview',
    nav: '',
    pages: [
      {
        path: '',
        title: 'Overview',
        blurb:
          "The day at a glance — today's numbers, orders needing action, next bookings, device health, channel status. Live via SSE.",
        absorbs: 'new — no v3 equivalent',
      },
    ],
  },
  {
    key: 'orders',
    label: 'Orders',
    nav: 'orders',
    pages: [
      {
        path: 'orders',
        title: 'Order feed',
        blurb:
          'Live day sheet + searchable history. Channel chips (POS, website, Uber Eats, Google), status filters, docket search. Accept/reject inline.',
        absorbs: 'absorbs: Online Order History (4 channel tabs), POS Order History',
      },
      {
        path: 'orders/:orderId',
        title: 'Order detail',
        blurb:
          "One page for any channel: lines, payments & refunds, status timeline, delivery state, integration events. Actions match the order's state machine.",
        absorbs: 'absorbs: Website / Uber Eats / Google order view pages',
      },
    ],
  },
  {
    key: 'menu',
    label: 'Menu',
    nav: 'menu',
    pages: [
      {
        path: 'menu',
        title: 'Menu',
        blurb:
          'Categories and items, move up/down reorder, availability switches, search. Category create/edit inline as a panel.',
        absorbs: 'absorbs: Menu, Category ×3 pages',
      },
      {
        path: 'menu/items/:itemId',
        title: 'Item editor',
        blurb:
          'One editor, five type presets (basic, pizza, half-&-half, deal, custom): pricing per size/channel, add-ons, GST, images, availability windows.',
        absorbs: 'absorbs: 10 create/edit pages (5 types × 2)',
      },
      {
        path: 'menu/pizza-settings',
        title: 'Pizza settings',
        blurb: 'Sizes, bases, sauces, topping catalog — the shared vocabulary pizza items build on.',
        absorbs: 'absorbs: Pizza Settings',
      },
      {
        path: 'menu/modifiers',
        title: 'Modifier groups',
        blurb: 'The choices an item asks for — cook level, sauce, extras — as reusable groups with per-option extras.',
        absorbs: 'absorbs: Modifier List / Create / Edit',
      },
      {
        path: 'menu/sold-out',
        title: 'Sold out today',
        blurb: 'The daily 86 list: everything currently unavailable, one tap to restore.',
        absorbs: 'new — POS parity',
      },
    ],
  },
  {
    key: 'bookings',
    label: 'Bookings',
    nav: 'bookings',
    pages: [
      {
        path: 'bookings',
        title: 'Day sheet',
        blurb:
          "Tonight's service in arrival order; confirm / seat / no-show inline. Date picker doubles as history.",
        absorbs: 'absorbs: Table Booking History',
      },
      {
        path: 'bookings/settings',
        title: 'Booking settings',
        blurb: 'Sessions, capacity, lead times, closed dates, confirmation messages.',
        absorbs: 'new — Part N surface',
      },
      {
        path: 'bookings/:bookingId',
        title: 'Booking detail',
        blurb: 'Guest, party, table, notes, status timeline, contact log.',
        absorbs: 'absorbs: Table Booking View',
      },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    nav: 'customers',
    pages: [
      {
        path: 'customers',
        title: 'Directory',
        blurb:
          'Search (name, phone, email), profile panel with order history, addresses, notes. Restaurant-scoped data — the same directory in every store.',
        absorbs: 'absorbs: Restaurant Customers',
      },
      {
        path: 'customers/import',
        title: 'Import',
        blurb: 'CSV upload with column mapping; runs as a tracked job, history of past imports.',
        absorbs: 'absorbs: Customers Import',
      },
      {
        path: 'customers/coupons',
        title: 'Coupons',
        blurb: 'List + inline editor: codes, rules, windows, redemption counts.',
        absorbs: 'absorbs: Coupon List / Create / Edit',
      },
      {
        path: 'customers/loyalty',
        title: 'Loyalty',
        blurb: 'Program configuration and issued-card ledger on one surface.',
        absorbs: 'absorbs: Loyalty Settings, Loyalty Cards',
      },
      {
        path: 'customers/reviews',
        title: 'Reviews',
        blurb: 'Post-order review stream, rating summary, prompt settings.',
        absorbs: 'absorbs: Google Review Settings (add-on)',
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    nav: 'reports',
    pages: [
      {
        path: 'reports',
        title: 'Report index',
        blurb:
          'The Part K catalog grouped the way the API groups it: sales & operations, money, customers & loyalty, timesheets, inventory.',
        absorbs: 'absorbs: Reports (3 unrouted tabs)',
      },
      {
        path: 'reports/:report',
        title: 'Report view',
        blurb:
          'One templated page per report: period grammar, store timezone, on-screen table + CSV / XLSX export. ~24 reports ride this template.',
        absorbs: 'absorbs: Transaction Report, Monthly Order Stats',
      },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    nav: 'settings',
    pages: [
      {
        path: 'settings',
        title: 'Settings',
        blurb: 'Grouped directory with search — the map of everything below.',
        absorbs: 'absorbs: Add-ons hub, sidebar sprawl',
      },
      {
        path: 'settings/profile',
        title: 'Store profile',
        blurb: 'Name, address, contact, timezone, images.',
        absorbs: 'absorbs: Store Edit',
      },
      {
        path: 'settings/hours',
        title: 'Hours & closures',
        blurb:
          'One weekly schedule with per-service overrides (online, POS, delivery), holidays and surcharge rules together.',
        absorbs: 'absorbs: Trading Hours ×2, Holidays, PH Surcharge',
      },
      {
        path: 'settings/ordering',
        title: 'Ordering & checkout',
        blurb: 'Channel enablement, ETA options, order flow, dine-in mode, delivery areas & fees.',
        absorbs: 'absorbs: Checkout, Online Ordering, Dine-in, Delivery Area',
      },
      {
        path: 'settings/payments',
        title: 'Payments',
        blurb: 'Providers (Stripe, Tyro, ANZ Worldline), payment types, platform fees view.',
        absorbs: 'absorbs: Payments, Fees, ANZ Worldline integration',
      },
      {
        path: 'settings/channels',
        title: 'Channels',
        blurb: 'Uber Eats (connection, menu sync, price uplift), Google Food, Uber Direct courier.',
        absorbs: 'absorbs: Integrations hub + 5 sub-pages',
      },
      {
        path: 'settings/devices',
        title: 'Devices & printing',
        blurb:
          'POS terminals & sessions, KDS devices, printers & print stations, kitchen routing (item → station), cash drawer.',
        absorbs: 'absorbs: Print Settings, Item Routing, KDS Settings, POS Settings',
      },
      {
        path: 'settings/tables',
        title: 'Tables & floor plan',
        blurb: 'Table map editor, table QR codes, dine-in table CRUD.',
        absorbs: 'absorbs: Table Map, Table Create/Edit',
      },
      {
        path: 'settings/team',
        title: 'Team',
        blurb: 'POS employees (PINs, roles, time clock) and store-level portal access.',
        absorbs: 'absorbs: Employee List, Store Users',
      },
      {
        path: 'settings/pos',
        title: 'POS appearance',
        blurb: 'POS UI layout, surcharges & discounts, receipt options.',
        absorbs: 'absorbs: POS Settings (operator half)',
      },
    ],
  },
];

export const RESTAURANT_PAGES: Page[] = [
  {
    path: '',
    title: 'Stores',
    blurb: 'Store list with health snapshot; create store.',
    absorbs: 'absorbs: Restaurant Home, Store Create',
  },
  {
    path: 'settings',
    title: 'Brand profile',
    blurb: 'Name, logo, timezone, country.',
    absorbs: 'absorbs: Restaurant Edit',
  },
  {
    path: 'members',
    title: 'Members',
    blurb: 'Restaurant users and roles; invitations.',
    absorbs: 'absorbs: Restaurant Users',
  },
  {
    path: 'reports',
    title: 'Brand reports',
    blurb: 'Cross-store rollups on the same report template.',
    absorbs: 'absorbs: Restaurant Monthly Stats',
  },
];

export const PLATFORM_PAGES: Page[] = [
  {
    path: 'restaurants',
    title: 'Restaurants',
    blurb: 'Browse/search all tenants (Spanner search index); provision new restaurants.',
    absorbs: 'absorbs: Browse, Restaurant Create',
  },
  {
    path: 'orders',
    title: 'Live orders',
    blurb: 'Cross-platform new-order monitor (SSE).',
    absorbs: 'absorbs: New Orders',
  },
  {
    path: 'reports',
    title: 'Platform reports',
    blurb: 'Monthly volumes, platform fees — same report template.',
    absorbs: 'absorbs: Platform Monthly Stats',
  },
  {
    path: 'printers',
    title: 'Printer registry',
    blurb: 'Global printer fleet + detail; covers cloud-kitchen device models.',
    absorbs: 'absorbs: Printers ×2, Sunmi Printers',
  },
  {
    path: 'google-food',
    title: 'Feed runs',
    blurb: 'Google Food feed run history and manual trigger.',
    absorbs: 'new — Part M surface',
  },
  {
    path: 'settings',
    title: 'Platform settings',
    blurb: 'Global configuration singleton.',
    absorbs: 'absorbs: Platform Settings',
  },
];
